import * as React from 'react'
import { cn } from '~/lib/utils'

// 미니멀 토글 스위치 — 숨긴 native checkbox + 커스텀 트랙/썸. 시맨틱 토큰만, 다크 자동 적응.
// 제어형 API({checked, onCheckedChange, disabled, id})는 그대로 유지한다.
export type ToggleProps = {
    checked: boolean
    onCheckedChange: (checked: boolean) => void
    disabled?: boolean
    id?: string
    className?: string
    label?: React.ReactNode
    children?: React.ReactNode
    'aria-label'?: string
    'aria-labelledby'?: string
}

/** 제어형 토글 스위치. */
function Toggle({
    checked,
    onCheckedChange,
    disabled,
    id,
    className,
    label,
    children,
    ...aria
}: ToggleProps) {
    const reactId = React.useId()
    const inputId = id ?? reactId
    const text = label ?? children

    return (
        <label
            htmlFor={inputId}
            className={cn(
                'text-foreground inline-flex items-center gap-2 text-[13px] select-none',
                disabled && 'opacity-50',
                !disabled && 'cursor-pointer',
                className
            )}>
            <input
                id={inputId}
                type='checkbox'
                role='switch'
                checked={checked}
                disabled={disabled}
                data-slot='toggle'
                onChange={(e) => onCheckedChange(e.target.checked)}
                className='peer sr-only'
                {...aria}
            />
            <span
                aria-hidden
                className={cn(
                    'peer-focus-visible:ring-ring/40 relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors peer-focus-visible:ring-2',
                    checked ? 'bg-primary' : 'bg-input'
                )}>
                <span
                    className={cn(
                        'inline-block size-4 rounded-full bg-white shadow-xs transition-transform',
                        checked ? 'translate-x-4' : 'translate-x-0.5'
                    )}
                />
            </span>
            {text != null && <span>{text}</span>}
        </label>
    )
}

export { Toggle }
